import {
  CompanyLookupSelect,
  CompanyLookupSelectProps,
} from "@certn/cascadia-react/patterns/company/CompanyLookupSelect";
import { slugifyPath } from "@certn/utils";
import { useJsonForms } from "@jsonforms/react";
import get from "lodash.get";
import { useCallback, useContext, useRef } from "react";

import { TextFieldControl } from "./TextFieldControl";
import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { SummaryItem } from "../components/SummaryItem";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "../wrappers/withCascadiaControlProps";

export type CompanyNameControlOptions = {
  /** Shared API path, e.g. `/api/internal/shared/fca-firms/`. */
  lookupApiPath: string;
  /** Query string param sent to the lookup API (default: `q`). */
  lookupQueryParam?: string;
  /** Response field used as the selected item key (default: `reference_number`). */
  lookupItemKey?: string;
  /** Response field used as the display label (default: `name`). */
  lookupItemLabel?: string;
  /** Sibling field path (relative to parent object) that enables the lookup when truthy. */
  searchableWhenFieldName: string;
  minSearchLength?: number;
};

function parentObjectPath(fieldPath: string): string {
  const lastDot = fieldPath.lastIndexOf(".");
  if (lastDot === -1) {
    return "";
  }
  return fieldPath.slice(0, lastDot);
}

function siblingFieldPath(parentPath: string, fieldName: string): string {
  return parentPath ? `${parentPath}.${fieldName}` : fieldName;
}

export function CompanyNameControl(props: CascadiaControlProps) {
  const jsonforms = useJsonForms();
  const { isSummary, formID } = useContext(CascadiaJsonFormContext);
  const options = (props.uischema?.options || {}) as CompanyNameControlOptions;
  const lookupQueryParam = options.lookupQueryParam ?? "q";
  const lookupItemKey = options.lookupItemKey ?? "reference_number";
  const lookupItemLabel = options.lookupItemLabel ?? "name";

  const parentPath = parentObjectPath(props.path);
  const companyName = (props.data as string | undefined) ?? "";
  const useSearchableLookup = Boolean(
    get(
      jsonforms?.core?.data,
      siblingFieldPath(parentPath, options.searchableWhenFieldName)
    )
  );

  const companyNameRef = useRef(companyName);
  companyNameRef.current = companyName;

  const handleDebouncedInputChange = useCallback((value: string) => {
    if (value === companyNameRef.current) {
      return;
    }
    props.setValue(value);
  }, []);

  if (!options.lookupApiPath || !options.searchableWhenFieldName) {
    return <TextFieldControl {...props} />;
  }

  if (isSummary) {
    return <SummaryItem label={props.label}>{companyName}</SummaryItem>;
  }

  if (!useSearchableLookup) {
    return <TextFieldControl {...props} />;
  }

  const testID = `${formID ? `${formID}-` : ""}${slugifyPath(props.path)}-company-lookup`;

  return (
    <CompanyLookupSelect
      label={props.label}
      isRequired={props.required}
      testID={testID}
      lookupApiPath={
        options.lookupApiPath as CompanyLookupSelectProps["lookupApiPath"]
      }
      lookupQueryParam={lookupQueryParam}
      lookupItemKey={lookupItemKey}
      lookupItemLabel={lookupItemLabel}
      minSearchLength={options.minSearchLength}
      initialInputValue={companyName}
      description={props.description}
      errors={props.errors}
      isInvalid={props.invalid}
      onBlur={props.setTouched}
      onDebouncedInputChange={handleDebouncedInputChange}
      onSelectionChange={(_itemKey, itemLabel) => {
        setTimeout(() => {
          props.setValue(itemLabel);
        });
      }}
    />
  );
}

CompanyNameControl.displayName = "CompanyNameControl";

export const CompanyNameControlRenderer =
  withCascadiaControlProps(CompanyNameControl);
